import React, { useRef, useState, useCallback } from 'react';
import { Check, Trash2 } from 'lucide-react';

const THRESHOLD = 80; // px needed to commit a swipe action
const MAX_PULL = 140;

/**
 * Wraps a notification card to allow Gmail-style swipes on touch devices:
 *   - swipe-RIGHT  -> mark as read (blue background + check icon)
 *   - swipe-LEFT   -> delete        (red background + trash icon)
 * Falls through to a regular click when tapped. No-op on desktop/mouse.
 */
export default function SwipeableNotificationCard({
  children,
  onSwipeRight,
  onSwipeLeft,
  canSwipeRight = true,
  canSwipeLeft = true,
}) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [removing, setRemoving] = useState(false);
  const startX = useRef(null);
  const startY = useRef(null);
  const locked = useRef(false); // locked to horizontal scroll once intent is clear

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    locked.current = false;
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (startX.current === null) return;
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;

    // Decide intent: if vertical dominates, give up horizontal drag so the
    // page can scroll normally.
    if (!locked.current) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) {
        startX.current = null;
        setDragging(false);
        setOffset(0);
        return;
      }
      if (Math.abs(dx) > 6) {
        locked.current = true;
      } else {
        return;
      }
    }

    // Clamp by allowed directions
    let next = dx;
    if (!canSwipeRight && next > 0) next = 0;
    if (!canSwipeLeft && next < 0) next = 0;
    next = Math.max(-MAX_PULL, Math.min(MAX_PULL, next));
    setOffset(next);
  }, [canSwipeLeft, canSwipeRight]);

  const onTouchEnd = useCallback(() => {
    if (startX.current === null) {
      setDragging(false);
      return;
    }
    setDragging(false);
    startX.current = null;

    if (offset >= THRESHOLD && canSwipeRight && onSwipeRight) {
      // fly off right, then fire
      setRemoving(true);
      setOffset(600);
      setTimeout(() => {
        onSwipeRight();
      }, 180);
    } else if (offset <= -THRESHOLD && canSwipeLeft && onSwipeLeft) {
      setRemoving(true);
      setOffset(-600);
      setTimeout(() => {
        onSwipeLeft();
      }, 180);
    } else {
      setOffset(0);
    }
  }, [offset, canSwipeLeft, canSwipeRight, onSwipeLeft, onSwipeRight]);

  const showRight = offset > 8 && canSwipeRight;
  const showLeft = offset < -8 && canSwipeLeft;

  return (
    <div className="relative overflow-hidden rounded-lg" data-testid="swipeable-notification">
      {/* Right-swipe (mark read) background */}
      {showRight && (
        <div
          className="absolute inset-0 flex items-center justify-start pl-5 bg-blue-500 text-white pointer-events-none"
          aria-hidden="true"
        >
          <Check className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Mark read</span>
        </div>
      )}
      {/* Left-swipe (delete) background */}
      {showLeft && (
        <div
          className="absolute inset-0 flex items-center justify-end pr-5 bg-red-500 text-white pointer-events-none"
          aria-hidden="true"
        >
          <span className="text-sm font-medium mr-2">Delete</span>
          <Trash2 className="h-5 w-5" />
        </div>
      )}

      <div
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? 'none' : 'transform 180ms ease-out',
          willChange: 'transform',
          touchAction: 'pan-y',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        aria-hidden={removing}
      >
        {children}
      </div>
    </div>
  );
}
